/**
 * TOSS Web Application - Error Boundary
 *
 * Top-level React error boundary. Catches render-time errors anywhere in the
 * component tree below it and shows a recoverable Russian fallback screen
 * instead of a white screen.
 *
 * React error boundaries must be class components — there is no hook equivalent.
 * The fallback is intentionally self-contained (no Router/store dependencies),
 * because the thing that crashed might be a provider above the app tree. It uses
 * `window.location` for navigation rather than React Router for the same reason.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RotateCw, House, TriangleAlert } from 'lucide-react';
import { Button } from './Button';

// ============================================================================
// Types
// ============================================================================

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. Receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// Fallback screen
// ============================================================================

/**
 * Default crash screen. Shared by ErrorBoundary (render errors outside the
 * router) and the router-level errorElement (render errors inside routes,
 * which the data router catches before they can reach ErrorBoundary).
 */
export function ErrorFallbackScreen({ error }: { error?: unknown }): JSX.Element {
  const handleReload = (): void => {
    window.location.reload();
  };

  const handleGoHome = (): void => {
    // Full navigation, not a Router push — Router may be the crashed subtree.
    window.location.assign('/');
  };

  const devMessage =
    import.meta.env.DEV && error != null
      ? error instanceof Error
        ? error.message
        : String(error)
      : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-secondary)] px-6 py-12">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-error-subdued)]">
          <TriangleAlert className="h-6 w-6 text-[var(--color-error)]" aria-hidden="true" />
        </div>

        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
          Что-то пошло не так
        </h1>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
          Произошла непредвиденная ошибка. Попробуйте перезагрузить страницу — обычно
          это помогает. Если ошибка повторяется, обратитесь к администратору.
        </p>

        <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row">
          <Button
            onClick={handleReload}
            leftIcon={<RotateCw className="h-4 w-4" />}
          >
            Перезагрузить
          </Button>
          <Button
            variant="secondary"
            onClick={handleGoHome}
            leftIcon={<House className="h-4 w-4" />}
          >
            На главную
          </Button>
        </div>

        {devMessage && (
          <pre className="mt-8 overflow-auto rounded-lg bg-[var(--color-bg-tertiary)] p-3 text-left text-xs text-[var(--color-text-secondary)]">
            {devMessage}
          </pre>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log for diagnostics. A real monitoring sink can hook in here later.
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Uncaught render error:', error, errorInfo);
    } else {
      console.error('[ErrorBoundary]', error);
    }
  }

  private handleReset = (): void => {
    // Clear the error so the tree re-renders. If the underlying cause persists
    // the boundary will simply catch again on the next render.
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      if (fallback) {
        return fallback(error, this.handleReset);
      }

      return <ErrorFallbackScreen error={error} />;
    }

    return children;
  }
}

export default ErrorBoundary;
